import { useEffect, useState } from 'react';
import axios from 'axios';
import { configurationService, Plan } from 'commons/services';
import { SettlementPeriodType } from 'commons/types';

export type SettlementFee = {
  fee: number;
  settlementPeriod: SettlementPeriodType;
};

export function extractSettlementFees(currentPlan: Plan): SettlementFee[] {
  const fees = currentPlan.anticipations
    .filter((anticipation) => anticipation.paymentMethod === 'credit_card')
    .map((anticipation) => {
      const installment = anticipation.installments.find(
        (installment) => installment.installmentNumber === 1,
      );

      if (!installment) return;

      return {
        fee: installment.fee,
        settlementPeriod: anticipation.settlementPeriod,
      };
    })
    .filter((self: SettlementFee | undefined): self is SettlementFee => !!self)
    .sort((a, b) => a.settlementPeriod - b.settlementPeriod); // sort ascending

  return fees;
}

export function extractWireTransferSettlementFees(
  currentPlan: Plan,
): SettlementFee[] {
  return currentPlan.anticipations
    .filter((anticipation) => anticipation.paymentMethod === 'wire_transfer')
    .flatMap((anticipation) => {
      const installment = anticipation.installments?.[0];
      if (!installment) return [];
      return [
        {
          fee: installment.fee,
          settlementPeriod: anticipation.settlementPeriod,
        },
      ];
    })
    .sort((a, b) => a.settlementPeriod - b.settlementPeriod);
}

// Wire transfer settles in one day, or same day for a store on D0, so the fee
// of any other period must not be shown.
export function pickWireTransferSettlementFee(
  fees: SettlementFee[],
  savedSettlementPeriod?: SettlementPeriodType,
): SettlementFee | undefined {
  const wantedPeriod = savedSettlementPeriod === 0 ? 0 : 1;
  return fees.find((fee) => fee.settlementPeriod === wantedPeriod);
}

/**
 * Shown when `GET /admin/plan` fails. The fees are hardcoded and may not match
 * the store's plan. It never offers 0: real D0 is only shown from the plan.
 */
export const FALLBACK_SETTLEMENT_FEES: SettlementFee[] = [
  { fee: 0.0629, settlementPeriod: 1 },
  { fee: 0.0419, settlementPeriod: 7 },
  { fee: 0.0329, settlementPeriod: 14 },
];

export function useSettlementFees(): {
  loading: boolean;
  settlementFees: SettlementFee[];
  wireTransferSettlementFees: SettlementFee[];
  usedFallback: boolean;
} {
  const [loading, setLoading] = useState(true);
  const [usedFallback, setUsedFallback] = useState(true);
  const [remoteSettlementFees, setRemoteSettlementFees] = useState<
    SettlementFee[]
  >(FALLBACK_SETTLEMENT_FEES);
  const [wireTransferSettlementFees, setWireTransferSettlementFees] = useState<
    SettlementFee[]
  >([]);

  useEffect(() => {
    const tryFetchSettlementFees = async () => {
      setLoading(true);

      try {
        const currentPlan = await configurationService.getStorePlan();
        setLoading(false);

        const fees = extractSettlementFees(currentPlan);
        if (fees.length) {
          setRemoteSettlementFees(fees);
          setUsedFallback(false);
        }
        setWireTransferSettlementFees(
          extractWireTransferSettlementFees(currentPlan),
        );
      } catch (error: any) {
        if (!axios.isAxiosError(error)) throw error;

        // It's quite possible that this API can't respond - yet - the plans fees for a Store, therefore we fallback to hardcoded fees.
        setLoading(false);
      }
    };

    void tryFetchSettlementFees();
  }, [setLoading, setRemoteSettlementFees]);

  return {
    loading,
    settlementFees: remoteSettlementFees,
    wireTransferSettlementFees,
    usedFallback,
  };
}
